import { useCallback, useEffect } from 'react';

import { isFunction } from 'lodash';
import { useLatest } from 'react-use';

import type { SyncEventPayload, SyncEventType } from '@/lib/sync/events';
import type { SyncState } from '@/lib/sync/store';
import { useSyncStore } from '@/lib/sync/store';

import { useCrossTabSync } from './use-cross-tab-sync';

export function useSyncedStoreState<T extends SyncEventType, V = SyncEventPayload[T]>(
  eventType: T,
  selector: (state: SyncState) => V,
  setter: (value: V) => void,
): [V, (value: V | ((prev: V) => V)) => void] {
  const { emit, on } = useCrossTabSync();
  const value = useSyncStore(selector);
  const latestValue = useLatest(value);
  const latestSetter = useLatest(setter);

  const setValue = useCallback(
    (newValue: V | ((prev: V) => V)) => {
      const nextValue = isFunction(newValue) ? newValue(latestValue.current) : newValue;
      latestSetter.current(nextValue);
      emit(eventType, nextValue as SyncEventPayload[T]);
    },
    [emit, eventType, latestValue, latestSetter],
  );

  useEffect(() => {
    return on(eventType, (payload: SyncEventPayload[T]) => {
      latestSetter.current(payload as V);
    });
  }, [on, eventType, latestSetter]);

  return [value, setValue];
}
